import { useState, useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Skeleton,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
} from '@aaimplatform/ui';
import { usePledges } from '../../hooks/usePledges';
import type { PledgeSummary, PledgeStatus } from '../../hooks/usePledges';
import {
  BACK_TO_INBOX,
  PLEDGE_SEARCH_PLACEHOLDER,
  formatCurrencyFull,
  listCoverage,
  pledgeCount,
  usePledgeSummaryColumns,
} from './pledge-display';

// ---------------------------------------------------------------------------
// Sprint C14 Lane B WP B-1: Pledge Inbox
//
// Renamed from PledgeQueue.tsx via `git mv` to preserve history. The Pledge
// Inbox is the FI operator's transit-state queue: items that require human
// triage before they reach a terminal/active state.
//
// Backing enum (canonical PledgeStatus from platform-admin.openapi.yaml):
//   PENDING, ACKNOWLEDGED, ACTIVE, RELEASED, EXPIRED, WITHDRAWN, DECLINED, RETURNED
//
// Brief requested chips for sub-states REVISION_REQUESTED, ACK_PENDING,
// FI_REVIEW, ESCALATED -- these are NOT in the canonical PledgeStatus enum
// and there is no PledgeReviewState enum. Tracked as follow-on
// C14->C15-11 (extend PledgeStatus / introduce PledgeReviewState). Until
// then the inbox renders chips for the available transit states only:
//   NEW                  -> PENDING    (consumer-submitted, awaiting FI ack)
//   RETURNED_TO_CONSUMER -> RETURNED   (FI returned to consumer for revision)
//   REJECTED             -> DECLINED   (FI rejected the pledge)
//   WITHDRAWN            -> WITHDRAWN  (consumer withdrew before acceptance)
//
// Default filter set excludes ACTIVE + ACKNOWLEDGED + RELEASED + EXPIRED so
// the inbox view shows only items that need operator attention.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Transit-state filter definitions
// ---------------------------------------------------------------------------

/**
 * Inbox transit states. Each chip carries its display label, the underlying
 * canonical PledgeStatus value it filters on, and a short tooltip-style
 * description.
 */
export const INBOX_TRANSIT_STATES: ReadonlyArray<{
  id: string;
  label: string;
  status: PledgeStatus;
  description: string;
}> = [
  { id: 'NEW', label: 'New', status: 'PENDING', description: 'Submitted by the customer; awaiting FI acknowledgment.' },
  { id: 'RETURNED_TO_CONSUMER', label: 'Returned to customer', status: 'RETURNED', description: 'FI returned the pledge to the customer for revision.' },
  { id: 'REJECTED', label: 'Rejected', status: 'DECLINED', description: 'FI declined the pledge.' },
  { id: 'WITHDRAWN', label: 'Withdrawn', status: 'WITHDRAWN', description: 'The customer withdrew the pledge before acceptance.' },
  // GH #2598. A pledge waiting on the member's contract signature is in no
  // other list view (Active Pledges reads ACKNOWLEDGED and ACTIVE only), so
  // without this chip staff cannot find one at all. It is a chip and never a
  // default: the default triage view still excludes the status, because no FI
  // action advances it (INBOX_EXCLUDED_STATUSES below).
  {
    id: 'AWAITING_MEMBER_SIGNATURE',
    label: 'Awaiting customer signature',
    status: 'AWAITING_SIGNATURE',
    description: 'Contract sent to the customer; nothing for the FI to do until the customer signs.',
  },
];

/** The canonical PledgeStatus a transit-state chip filters on, if any. */
export function resolveTransitStatus(chipId: string | undefined): PledgeStatus | undefined {
  if (!chipId) return undefined;
  return INBOX_TRANSIT_STATES.find((s) => s.id === chipId)?.status;
}

/**
 * Statuses that the inbox view explicitly EXCLUDES from the default,
 * unfiltered view. Pledges in these states belong on the Active Pledges
 * page (B-2) or are otherwise terminal and not actionable in an inbox
 * triage workflow.
 */
export const INBOX_EXCLUDED_STATUSES: ReadonlyArray<PledgeStatus> = [
  'ACTIVE',
  'ACKNOWLEDGED',
  'RELEASED',
  'EXPIRED',
  // GH #2300. This list is an EXCLUSION, so omission over-includes -- the
  // opposite direction from every other status set. An awaiting-signature
  // pledge is waiting on the MEMBER, and no FI action can advance it, so
  // leaving it in the default triage view fills the inbox with rows staff
  // cannot act on. It enters the inbox as PENDING once signed, and it stays
  // reachable through the 'Awaiting customer signature' chip (GH #2598),
  // which bypasses this list.
  //
  // GH #2824. Three lists answer three questions (Sprint C14 B-2). The inbox
  // default view triages new work. Active Pledges carries ACKNOWLEDGED and
  // ACTIVE. Tasks (task.service TASK_VISIBLE_STATUSES) and the digest
  // (digest.service PENDING_FI_ACTION_STATUSES) count all FI-owed work,
  // PENDING and ACKNOWLEDGED. So this list and the FI-owed sets overlap on
  // ACKNOWLEDGED alone: owed by the FI, and worked from Active Pledges.
  // AWAITING_SIGNATURE is in neither FI-owed set. PledgeInbox.test.tsx pins
  // the overlap.
  'AWAITING_SIGNATURE',
];

/**
 * The rows the inbox shows. With no chip, the default triage view drops the
 * excluded statuses. With a chip, the server-side status filter is
 * authoritative and nothing is dropped, which is how an excluded status such
 * as AWAITING_SIGNATURE stays reachable.
 */
export function scopeInboxPledges(
  pledges: ReadonlyArray<PledgeSummary>,
  statusFilter: PledgeStatus | undefined,
): PledgeSummary[] {
  if (statusFilter) return [...pledges];
  return pledges.filter((p) => !INBOX_EXCLUDED_STATUSES.includes(p.status));
}

// ---------------------------------------------------------------------------
// FilterBar definitions
// ---------------------------------------------------------------------------

const TRANSIT_STATE_FILTER: FilterDefinition = {
  id: 'transitState',
  label: 'Transit state',
  type: 'select',
  options: INBOX_TRANSIT_STATES.map((s) => ({ value: s.id, label: s.label })),
};

const FILTER_DEFINITIONS: FilterDefinition[] = [
  TRANSIT_STATE_FILTER,
  {
    id: 'member',
    label: 'Customer',
    type: 'text',
    placeholder: 'Search by customer name or email...',
  },
];

/**
 * What an empty inbox grid says. The grid's default read "No data available"
 * and never said what was absent or why (fi-g1-11). A failed read is not an
 * empty inbox, and a chip or search that matches nothing is not one either.
 */
export function inboxEmptyMessage({
  failed,
  narrowed,
}: {
  failed: boolean;
  narrowed: boolean;
}): string {
  if (failed) return 'Pledges could not be read, so none are listed.';
  if (narrowed) return 'No pledges match this state or search.';
  return 'No pledges are waiting for review. Accepted pledges are listed under Active Pledges.';
}

// ---------------------------------------------------------------------------
// PledgeInboxPage
// ---------------------------------------------------------------------------

export function PledgeInboxPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const columns = usePledgeSummaryColumns(BACK_TO_INBOX);

  // Resolve the active transit-state chip (if any) to its underlying
  // canonical PledgeStatus for the API filter.
  const transitStateChip = activeFilters.find((f) => f.filterId === 'transitState')
    ?.value as string | undefined;
  const statusFilter = useMemo<PledgeStatus | undefined>(
    () => resolveTransitStatus(transitStateChip),
    [transitStateChip],
  );

  // Fetch from /api/admin/pledges. When a chip is selected, narrow to that
  // single status server-side. When no chip is selected, fetch the full set
  // and apply the inbox exclusion filter client-side.
  const { data: response, isLoading, error } = usePledges(
    statusFilter ? { status: statusFilter } : undefined,
  );

  const pledges = response?.pledges ?? [];

  // Default-view inbox exclusion: when no chip is active, hide pledges in
  // ACTIVE / ACKNOWLEDGED / RELEASED / EXPIRED states (those belong on the
  // Active Pledges page or are terminal). When a chip IS active, the
  // server-side status filter is authoritative -- no further filtering.
  const inboxScopedPledges = useMemo(
    () => scopeInboxPledges(pledges, statusFilter),
    [pledges, statusFilter],
  );

  const filteredPledges = useMemo(() => {
    if (!searchQuery.trim()) return inboxScopedPledges;
    const query = searchQuery.toLowerCase();
    return inboxScopedPledges.filter(
      (p) =>
        p.memberEmail.toLowerCase().includes(query) ||
        (p.memberName ?? '').toLowerCase().includes(query) ||
        p.collateralPackageId.toLowerCase().includes(query),
    );
  }, [inboxScopedPledges, searchQuery]);

  // ---------------------------------------------------------------------------
  // Summary metrics
  // ---------------------------------------------------------------------------

  // A tile may describe the inbox only after a read produced one. A failed
  // read, or a paused first fetch (no data and no error), withholds every
  // figure with the reason instead of printing a 0 nobody measured.
  const withheldCaption: string | undefined = error
    ? METRIC_READ_CAPTIONS.failed
    : response
      ? undefined
      : METRIC_READ_CAPTIONS.unread;
  const withheld = withheldCaption !== undefined;

  // The list endpoint pages (limit default 20). `response.total` counts every
  // pledge the server holds for the requested status, or for ALL statuses when
  // no chip is active -- the ACTIVE ones this inbox excludes included. So the
  // inbox figures are counted from the rows this page read, and the caption
  // says how many that was out of the server total.
  const coverage = listCoverage(pledges.length, response?.total);
  const totalScope = statusFilter ? 'with this status' : 'in all states';
  const inboxCaption = coverage.partial
    ? `Counted from ${coverage.read} of ${pledgeCount(coverage.total ?? coverage.read)} ${totalScope}`
    : `${pledgeCount(coverage.total ?? coverage.read)} ${totalScope}`;

  const newCount = useMemo(
    () => filteredPledges.filter((p) => p.status === 'PENDING').length,
    [filteredPledges],
  );

  const returnedCount = useMemo(
    () => filteredPledges.filter((p) => p.status === 'RETURNED').length,
    [filteredPledges],
  );

  const totalCollateral = useMemo(
    () =>
      filteredPledges.reduce((sum, p) => sum + (p.totalCollateralValue ?? 0), 0),
    [filteredPledges],
  );

  const totalPledgeable = useMemo(
    () =>
      filteredPledges.reduce(
        (sum, p) => sum + (p.totalPledgeableAmount ?? 0),
        0,
      ),
    [filteredPledges],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load pledges: {error.message}
          </div>
        </div>
      )}

      <MetricCardGrid className="mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
              <Skeleton className="h-10 w-12 mb-3" />
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))
        ) : (
          <>
            {/* Every caption here is a description of the figure, so it takes
                the sans description slot. MetricCard's `change` slot is set in
                monospace and colored as a movement, which read an absence
                ("None new") as a green gain (fi-g1-15). */}
            <MetricCard
              label="Inbox items"
              value={withheld ? '--' : filteredPledges.length.toString()}
              caption={withheldCaption}
              description={withheld ? undefined : inboxCaption}
            />
            <MetricCard
              label="New"
              value={withheld ? '--' : newCount.toString()}
              caption={withheldCaption}
              description={
                withheld ? undefined : newCount > 0 ? 'Awaiting acknowledgment' : 'None new'
              }
            />
            <MetricCard
              label="Returned to customer"
              value={withheld ? '--' : returnedCount.toString()}
              caption={withheldCaption}
              description={withheld ? undefined : 'Awaiting customer revision'}
            />
            <MetricCard
              label="Total pledgeable in inbox"
              value={withheld || coverage.partial ? '--' : formatCurrencyFull(totalPledgeable)}
              caption={withheldCaption}
              description={
                withheld
                  ? undefined
                  : coverage.partial
                    ? inboxCaption
                    : `${formatCurrencyFull(totalCollateral)} collateral value`
              }
            />
          </>
        )}
      </MetricCardGrid>

      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        // Short enough to fit the phone field; the placeholder doubles as the
        // input's accessible name, so it lists what the search reads (fi-g1-17).
        searchPlaceholder={PLEDGE_SEARCH_PLACEHOLDER}
        className="rounded-t-sm"
      />

      {isLoading ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<PledgeSummary>
          data={filteredPledges}
          columns={columns}
          emptyMessage={inboxEmptyMessage({
            failed: Boolean(error),
            narrowed: Boolean(statusFilter) || searchQuery.trim() !== '',
          })}
          enablePagination
          defaultPageSize={20}
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => row.id}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
